import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import { Check, Loader2, Sparkles } from "lucide-react";
import { Card, ClientName, clientDisplayName } from "../components/ui";
import {
  GET_SESSION_DETAIL,
  GET_PRIMARY_CUSTOMER,
  GET_PRICING_PLAN,
  GET_SESSION_BILLABLE,
  GET_SESSION_TRANSCRIPT,
  GENERATE_CLOSURE_SUMMARY,
  CLOSE_BOOKING,
} from "../apollo/sessions";
import { methodToLocation } from "../utils/sessionTime";

// Server DATETIMEs serialize as naive UTC ISO strings (no offset). A bare
// `new Date(s)` would parse those as LOCAL time — off by the viewer's UTC
// offset (this is what made the transcript ETA show ~7 hours).
function parseUtc(s: string): string {
  return /Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`;
}

function timelineLabel(eventType: string, role: string | null): string {
  const who =
    role === "customer" ? "Client" : role ? "Provider" : "";
  switch (eventType) {
    case "room-created":
      return "Room created";
    case "participant-connected":
      return `${who || "Participant"} joined`;
    case "participant-disconnected":
      return `${who || "Participant"} left`;
    case "session-ended":
      return "Session ended";
    case "meeting-ended-by-provider":
      return "Meeting ended for everyone";
    default:
      return eventType.replace(/-/g, " ");
  }
}

function formatStartTime(date: string, startTime: string): string {
  const iso = /([zZ]|[+\-]\d\d:?\d\d)$/.test(startTime.trim())
    ? `${date}T${startTime.trim()}`
    : `${date}T${startTime.trim()}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function timelineTime(occurredAt: string): string {
  return new Date(parseUtc(occurredAt)).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Minute input helper - defined outside to prevent remounting on parent re-renders
function MinuteInput({
  label,
  value,
  onChange,
  amount,
  hourlyRate,
  submitting,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  amount: number;
  hourlyRate: number;
  submitting: boolean;
}) {
  return (
    <div>
      <label className="block text-[12px] text-ink-3 font-medium mb-1.5">{label}</label>
      <input
        type="number"
        min="0"
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={submitting}
        className="w-full px-3 py-2 rounded-md hair border bg-paper text-[13px] text-ink-2 num focus:outline-none focus:ring-2 focus:ring-tide/30"
      />
      {hourlyRate > 0 && (
        <div className="text-[11px] text-ink-4 mt-1 num">${amount.toFixed(2)}</div>
      )}
    </div>
  );
}

export function SessionClose() {
  const { id: bookingId } = useParams();
  const navigate = useNavigate();

  // Form state
  const [preparationMinutes, setPreparationMinutes] = useState("0");
  const [meetingMinutes, setMeetingMinutes] = useState("0");
  const [followupMinutes, setFollowupMinutes] = useState("0");
  const [extraMinutes, setExtraMinutes] = useState("0");
  const [discountPercentage, setDiscountPercentage] = useState("0");
  const [closureMessage, setClosureMessage] = useState("");

  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(8);
  const [error, setError] = useState<string | null>(null);

  // Data
  const { data: bookingData, loading: bookingLoading } = useQuery(GET_SESSION_DETAIL, {
    variables: { id: bookingId },
    skip: !bookingId,
  });

  const { data: customerData } = useQuery(GET_PRIMARY_CUSTOMER, {
    variables: { filter: { bookingId, isPrimary: true } },
    skip: !bookingId,
  });

  const booking = bookingData?.Booking;

  const { data: pricingData } = useQuery(GET_PRICING_PLAN, {
    variables: { id: booking?.applicablePricingPlanId },
    skip: !booking?.applicablePricingPlanId,
  });

  const [closeBooking, { loading: submitting }] = useMutation(CLOSE_BOOKING);
  const [generateClosureSummary] = useMutation(GENERATE_CLOSURE_SUMMARY);

  const primaryCustomer = customerData?.allPeople?.[0] ?? null;
  const hourlyRate = pricingData?.PricingPlan?.hourlyRate ?? 0;

  // ── Meeting-minutes prefill ─────────────────────────────────────────
  // Billable overlap time (provider + customer connected together),
  // rounded UP to the nearest minute. Applied once, and only while the
  // field still holds its untouched "0" default — never clobbers an edit.
  const { data: billableData } = useQuery(GET_SESSION_BILLABLE, {
    variables: { bookingId },
    skip: !bookingId,
  });
  const prefillDoneRef = useRef(false);
  const sessionNotes: string = (
    billableData?.sessionStatus?.notes?.[0]?.content ?? ""
  ).trim();
  interface CloseActionItem {
    id: string;
    text: string;
    isCompleted: boolean;
    sortOrder: number;
  }
  const sessionActionItems: CloseActionItem[] = (
    billableData?.sessionStatus?.actionItems ?? []
  )
    .slice()
    .sort((a: CloseActionItem, b: CloseActionItem) => a.sortOrder - b.sortOrder);

  // Major lifecycle events for the timeline card. occurredAt comes back as
  // naive UTC ISO (no offset) — append Z so the browser doesn't parse it
  // as local time.
  const TIMELINE_EVENTS = new Set([
    "room-created",
    "participant-connected",
    "participant-disconnected",
    "session-ended",
    "meeting-ended-by-provider",
  ]);
  interface CloseEvent {
    id: string;
    eventType: string;
    participantRole: string | null;
    occurredAt: string | null;
  }
  const timelineEvents: CloseEvent[] = (
    billableData?.sessionStatus?.events ?? []
  )
    .filter((e: CloseEvent) => TIMELINE_EVENTS.has(e.eventType) && e.occurredAt)
    .slice()
    .sort(
      (a: CloseEvent, b: CloseEvent) =>
        new Date(parseUtc(a.occurredAt!)).getTime() -
        new Date(parseUtc(b.occurredAt!)).getTime(),
    );
  useEffect(() => {
    if (prefillDoneRef.current) return;
    const elapsed = billableData?.sessionStatus?.elapsedSecondsBillable ?? 0;
    if (elapsed > 0) {
      prefillDoneRef.current = true;
      setMeetingMinutes((current) =>
        current === "0" ? String(Math.ceil(elapsed / 60)) : current,
      );
    }
  }, [billableData]);

  // ── AI closure-message draft ────────────────────────────────────────
  // Poll the transcript pipeline until it settles; once a transcript
  // exists, ask hd-api for a Claude-drafted closure email (cached
  // server-side). The draft is OFFERED via a button — it never
  // auto-replaces whatever the provider typed.
  const [aiDraft, setAiDraft] = useState<string | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const draftRequestedRef = useRef(false);

  const {
    data: transcriptData,
    startPolling: startTranscriptPolling,
    stopPolling: stopTranscriptPolling,
  } = useQuery(GET_SESSION_TRANSCRIPT, {
    variables: { bookingId },
    skip: !bookingId,
  });
  const transcriptStage: string | null =
    transcriptData?.sessionTranscript?.stage ?? null;
  const transcriptSettled =
    transcriptStage === "ready" ||
    transcriptStage === "partial" ||
    transcriptStage === "failed";

  // Human ETA for the suggested message, from the resolver's
  // estimatedReadyAt (ended_at + merge grace + margin). Recomputed on
  // every poll response, so it stays fresh without a dedicated ticker.
  const summaryEta = useMemo(() => {
    const readyAt = transcriptData?.sessionTranscript?.estimatedReadyAt;
    if (!readyAt) return null;
    const ms = new Date(parseUtc(readyAt)).getTime() - Date.now();
    if (ms <= 15_000) return "any moment now";
    if (ms <= 90_000) return "in under a minute";
    return `in ~${Math.ceil(ms / 60_000)} min`;
  }, [transcriptData]);

  useEffect(() => {
    if (!bookingId) return;
    if (transcriptSettled) {
      stopTranscriptPolling();
    } else {
      startTranscriptPolling(10000);
    }
    return () => stopTranscriptPolling();
  }, [bookingId, transcriptSettled, startTranscriptPolling, stopTranscriptPolling]);

  useEffect(() => {
    if (draftRequestedRef.current) return;
    if (transcriptStage !== "ready" && transcriptStage !== "partial") {
      if (transcriptStage === "failed") setAiUnavailable(true);
      return;
    }
    draftRequestedRef.current = true;
    setAiGenerating(true);
    generateClosureSummary({ variables: { bookingId } })
      .then(({ data }) => {
        const res = data?.generateClosureSummary;
        if (res?.success && res.summary) {
          setAiDraft(res.summary);
        } else {
          setAiUnavailable(true);
        }
      })
      .catch(() => setAiUnavailable(true))
      .finally(() => setAiGenerating(false));
  }, [transcriptStage, bookingId, generateClosureSummary]);

  // Calculate billing in real-time
  const amounts = useMemo(() => {
    const prep = parseInt(preparationMinutes) || 0;
    const meeting = parseInt(meetingMinutes) || 0;
    const followup = parseInt(followupMinutes) || 0;
    const extra = parseInt(extraMinutes) || 0;
    const discount = parseFloat(discountPercentage) || 0;

    const totalMinutes = prep + meeting + followup + extra;
    const subtotal = (totalMinutes / 60) * hourlyRate;
    const discountAmount = subtotal * (discount / 100);
    const total = subtotal - discountAmount;

    return {
      prepAmount: (prep / 60) * hourlyRate,
      meetingAmount: (meeting / 60) * hourlyRate,
      followupAmount: (followup / 60) * hourlyRate,
      extraAmount: (extra / 60) * hourlyRate,
      totalMinutes,
      subtotal,
      discountAmount,
      total,
    };
  }, [preparationMinutes, meetingMinutes, followupMinutes, extraMinutes, discountPercentage, hourlyRate]);

  // Countdown redirect
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate(`/sessions/${bookingId}`);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [success, countdown, navigate, bookingId]);

  const handleSubmit = async () => {
    setError(null);

    const prep = parseInt(preparationMinutes);
    const meeting = parseInt(meetingMinutes);
    const followup = parseInt(followupMinutes);
    const extra = parseInt(extraMinutes);
    const discount = parseFloat(discountPercentage);

    // Validate
    if (
      isNaN(prep) || prep < 0 ||
      isNaN(meeting) || meeting < 0 ||
      isNaN(followup) || followup < 0 ||
      isNaN(extra) || extra < 0
    ) {
      setError("All minute fields must be non-negative integers.");
      return;
    }
    if (isNaN(discount) || discount < 0 || discount > 100) {
      setError("Discount percentage must be between 0 and 100.");
      return;
    }
    if (!closureMessage.trim()) {
      setError("Please enter a closure message.");
      return;
    }

    try {
      const result = await closeBooking({
        variables: {
          bookingId,
          preparationMinutes: prep,
          meetingMinutes: meeting,
          followupMinutes: followup,
          extraMinutes: extra,
          discountPercentage: discount,
          closureMessage: closureMessage.trim(),
        },
      });
      if (result.data?.closeBooking) {
        setSuccess(true);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to close booking";
      setError(message);
    }
  };

  // Loading
  if (bookingLoading || !booking) {
    return (
      <div className="px-7 pt-10 pb-20">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-48 rounded bg-canvas" />
          <div className="h-10 w-72 rounded bg-canvas" />
          <div className="h-64 rounded-lg bg-canvas" />
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="px-7 pt-10 pb-20 max-w-[720px] mx-auto">
        <div className="bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-sage mx-auto mb-4 flex items-center justify-center">
            <Check size={24} strokeWidth={2.5} className="text-paper-hi" />
          </div>
          <h2 className="font-display text-[24px] font-medium mb-2">Session closed</h2>
          <p className="text-[14px] text-ink-3 mb-4">
            The session has been closed and a charge has been created. The customer
            will be notified.
          </p>
          <p className="text-[12.5px] text-ink-4">
            Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-7 pt-10 pb-20 max-w-[1200px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[12.5px] mb-6">
        <Link to="/sessions" className="text-ink-4 hover:text-ink-2">Sessions</Link>
        <span className="text-ink-5">›</span>
        <Link to={`/sessions/${bookingId}`} className="text-ink-4 hover:text-ink-2">
          {primaryCustomer ? clientDisplayName(primaryCustomer) : (booking.primaryCustomerName ?? "Session")}
        </Link>
        <span className="text-ink-5">›</span>
        <span className="text-ink font-medium">Close</span>
      </div>

      <h1 className="font-display text-[32px] font-medium leading-[1.1] tracking-tight mb-6">
        Close session
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Left: the closing workflow */}
        <div className="lg:col-span-2 space-y-4">
        {/* Booking summary */}
        <Card header={{ title: "Booking summary" }}>
          <div className="p-5 space-y-4">
            {primaryCustomer && (
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Customer
                </div>
                <div className="text-[13px] font-medium">
                  <ClientName customer={primaryCustomer} showSecondary secondaryClassName="text-[11.5px] text-ink-4 font-normal" />
                </div>
              </div>
            )}
            {/* Bottom row: Method · Time · Status */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Method
                </div>
                <div className="text-[13px] font-medium">{methodToLocation(booking.method)}</div>
              </div>
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Time
                </div>
                <div className="text-[13px] font-medium">
                  {booking.availability
                    ? formatStartTime(
                        booking.availability.date,
                        booking.availability.startTime,
                      )
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                  Status
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap text-gold bg-gold/10">
                  Pre-Billing
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Time tracking */}
        <Card header={{ title: "Time tracking" }}>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-4">
            <MinuteInput
              label="Preparation (min)"
              value={preparationMinutes}
              onChange={setPreparationMinutes}
              amount={amounts.prepAmount}
              hourlyRate={hourlyRate}
              submitting={submitting}
            />
            <MinuteInput
              label="Meeting (min)"
              value={meetingMinutes}
              onChange={setMeetingMinutes}
              amount={amounts.meetingAmount}
              hourlyRate={hourlyRate}
              submitting={submitting}
            />
            <MinuteInput
              label="Follow-up (min)"
              value={followupMinutes}
              onChange={setFollowupMinutes}
              amount={amounts.followupAmount}
              hourlyRate={hourlyRate}
              submitting={submitting}
            />
            <MinuteInput
              label="Extra (min)"
              value={extraMinutes}
              onChange={setExtraMinutes}
              amount={amounts.extraAmount}
              hourlyRate={hourlyRate}
              submitting={submitting}
            />
          </div>

            <div className="mt-4">
              <label className="block text-[12px] text-ink-3 font-medium mb-1.5">
                Discount (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(e.target.value)}
                disabled={submitting}
                className="w-full max-w-[200px] px-3 py-2 rounded-md hair border bg-paper text-[13px] text-ink-2 num focus:outline-none focus:ring-2 focus:ring-tide/30"
              />
              <div className="text-[11px] text-ink-4 mt-1">Enter a value between 0 and 100</div>
            </div>
          </div>
        </Card>

        {/* Billing summary */}
        <Card header={{ title: "Billing summary" }}>
          <div className="p-5 space-y-2">
            <div className="flex justify-between text-[13px]">
              <span className="text-ink-3">Total minutes</span>
              <span className="font-medium num">{amounts.totalMinutes} min</span>
            </div>
            {hourlyRate > 0 && (
              <>
                <div className="flex justify-between text-[13px]">
                  <span className="text-ink-3">
                    Subtotal (@ ${hourlyRate}/hr)
                  </span>
                  <span className="font-medium num">${amounts.subtotal.toFixed(2)}</span>
                </div>
                {amounts.discountAmount > 0 && (
                  <div className="flex justify-between text-[13px]">
                    <span className="text-ink-3">Discount</span>
                    <span className="font-medium text-sage num">
                      -${amounts.discountAmount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="hair-t pt-2 mt-2 flex justify-between text-[15px]">
                  <span className="font-semibold">Total</span>
                  <span className="font-display text-[18px] font-medium text-tide num">
                    ${amounts.total.toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Closure message */}
        <Card header={{ title: "Closure message" }}>
          <div className="p-5">
            {/* AI draft states — informational; the textarea below is always
                editable and never auto-filled. */}
            {transcriptData?.sessionTranscript && !transcriptSettled && (
              <div className="mb-4 flex items-center gap-2.5 rounded-md bg-paper px-4 py-3 text-[12.5px] text-ink-3">
                <Loader2 size={14} strokeWidth={2} className="animate-spin text-tide shrink-0" />
                <span>
                  Session recording is being processed — a suggested message
                  will appear here{" "}
                  {summaryEta ? (
                    <span className="font-medium text-ink-2">{summaryEta}</span>
                  ) : (
                    "when it's ready"
                  )}
                  .
                </span>
              </div>
            )}
            {aiGenerating && (
              <div className="mb-4 flex items-center gap-2.5 rounded-md bg-paper px-4 py-3 text-[12.5px] text-ink-3">
                <Loader2 size={14} strokeWidth={2} className="animate-spin text-tide shrink-0" />
                Drafting a suggested message from the transcript…
              </div>
            )}
            {aiDraft && (
              <div className="mb-4 rounded-md hair border bg-paper p-4">
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-tide mb-2">
                  <Sparkles size={12} strokeWidth={2} />
                  Suggested message
                </div>
                <p className="text-[12.5px] text-ink-2 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {aiDraft}
                </p>
                <button
                  type="button"
                  onClick={() => setClosureMessage(aiDraft)}
                  disabled={submitting || closureMessage === aiDraft}
                  className="mt-3 px-3.5 py-2 rounded-md bg-tide text-paper-hi text-[12px] font-medium hover:bg-tide-d disabled:opacity-50"
                >
                  {closureMessage === aiDraft
                    ? "Draft inserted"
                    : closureMessage.trim()
                      ? "Replace my message with this draft"
                      : "Use this draft"}
                </button>
              </div>
            )}
            {aiUnavailable && !aiDraft && (
              <div className="mb-4 text-[12px] text-ink-4">
                No transcript summary available — write your own message below.
              </div>
            )}

            <textarea
              value={closureMessage}
              onChange={(e) => setClosureMessage(e.target.value)}
              placeholder="Enter a message that will be sent to the customer via email..."
              rows={8}
              disabled={submitting}
              className="w-full p-3 rounded-md bg-paper hair border text-[13px] text-ink-2 placeholder:text-ink-5 focus:outline-none focus:ring-2 focus:ring-tide/30 resize-y min-h-[120px]"
            />
            <div className="text-[11px] text-ink-4 mt-1.5">
              This message will be included in the closure email sent to the customer.
            </div>
          </div>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-rust-s rounded-md px-4 py-3 text-[13px] text-rust flex items-start gap-2">
            <span className="font-medium">Error:</span> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-5 py-2.5 rounded-md bg-tide text-paper-hi text-[13.5px] font-medium hover:bg-tide-d disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Closing..." : "Close & Bill Session"}
          </button>
          <Link
            to={`/sessions/${bookingId}`}
            className="px-5 py-2.5 rounded-md hair border bg-paper-hi text-[13.5px] text-ink-2 hover:bg-paper-2 font-medium"
          >
            Go back
          </Link>
        </div>
        </div>

        {/* Right: what happened during the session */}
        <div className="space-y-4">
          {/* Session timeline — what actually happened, not what was
              scheduled */}
          <Card header={{ title: "Session timeline" }}>
            <div className="p-5">
              {timelineEvents.length === 0 ? (
                <div className="text-[12.5px] text-ink-4">
                  No session events were recorded for this booking.
                </div>
              ) : (
                <div className="space-y-0">
                  {timelineEvents.map((e, idx) => (
                    <div key={e.id} className="flex items-start gap-3">
                      <div className="flex flex-col items-center self-stretch">
                        <span
                          className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${
                            e.eventType.includes("connected") &&
                            !e.eventType.includes("dis")
                              ? "bg-sage"
                              : e.eventType.includes("disconnected")
                                ? "bg-gold"
                                : e.eventType.includes("ended")
                                  ? "bg-rust"
                                  : "bg-tide"
                          }`}
                        />
                        {idx < timelineEvents.length - 1 && (
                          <span className="w-px flex-1 min-h-[14px] bg-ink/10" />
                        )}
                      </div>
                      <div className="flex items-baseline gap-2.5 pb-3 min-w-0">
                        <span className="text-[11.5px] font-mono text-ink-4 num w-[64px] flex-shrink-0">
                          {timelineTime(e.occurredAt!)}
                        </span>
                        <span className="text-[12.5px] text-ink-2">
                          {timelineLabel(e.eventType, e.participantRole)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Session notes — captured during the call */}
          <Card header={{ title: "Session notes" }}>
            <div className="p-5">
              {sessionNotes ? (
                <p className="text-[13px] text-ink-2 whitespace-pre-wrap leading-relaxed">
                  {sessionNotes}
                </p>
              ) : (
                <div className="text-[12.5px] text-ink-4">
                  No notes were taken during this session.
                </div>
              )}
            </div>
          </Card>

          {/* Action items — captured during the call */}
          <Card header={{ title: "Action items" }}>
            <div className="p-5">
              {sessionActionItems.length === 0 ? (
                <div className="text-[12.5px] text-ink-4">
                  No action items were captured during this session.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {sessionActionItems.map((item) => (
                    <div key={item.id} className="flex items-start gap-2.5">
                      <span
                        className={`w-4 h-4 mt-0.5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                          item.isCompleted
                            ? "bg-sage border-sage text-paper-hi"
                            : "border-ink-5"
                        }`}
                      >
                        {item.isCompleted && <Check size={9} strokeWidth={3} />}
                      </span>
                      <span
                        className={`text-[13px] leading-snug ${
                          item.isCompleted
                            ? "text-ink-4 line-through"
                            : "text-ink-2"
                        }`}
                      >
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
